/**
 * Header Component
 * 메인 페이지: 초기 transparent(흰 로고·흰 텍스트), 스크롤 또는 헤더 호버 시 is-scrolling → default 상태
 */
export class Header {
    constructor() {
        this.header = document.getElementById('main-header');
        this.drawer = document.getElementById('main-drawer');
        
        if (!this.header) return;
        
        this.isMain = document.body.classList.contains('is-main');
        this.init();
    }

    init() {
        this.initNav();
        this.initDrawer();
        if (this.isMain) {
            this.initScrollingState();
        }
    }

    /**
     * 메인 페이지 전용: 스크롤이 header-height 이상이거나 헤더에 마우스 오버 시 is-scrolling 추가 → default 스타일
     */
    initScrollingState() {
        const header = this.header;

        const updateScrolling = () => {
            const headerHeight = header.offsetHeight;
            const scrollY = window.scrollY ?? window.pageYOffset;
            const isPastHeader = scrollY >= headerHeight - 1;
            if (isPastHeader) {
                header.classList.add('is-scrolling');
            } else {
                header.classList.remove('is-scrolling');
            }
        };

        const onScroll = () => {
            requestAnimationFrame(updateScrolling);
        };

        const onMouseEnter = () => {
            header.classList.add('is-scrolling');
        };

        const onMouseLeave = () => {
            const headerHeight = header.offsetHeight;
            const scrollY = window.scrollY ?? window.pageYOffset;
            if (scrollY < headerHeight - 1) {
                header.classList.remove('is-scrolling');
            }
        };

        updateScrolling();
        window.addEventListener('scroll', onScroll, { passive: true });
        header.addEventListener('mouseenter', onMouseEnter);
        header.addEventListener('mouseleave', onMouseLeave);
    }

    initNav() {
        const nav = this.header.querySelector('.no-header__nav');
        if (!nav) return;

        if (typeof gsap !== 'undefined') {
            const tl = gsap.timeline({ paused: true });
            
            tl.to(this.header, {
                height: () => nav.offsetHeight + 'px',
                ease: 'power3.inOut',
            });

            const onEnter = () => {
                this.header.setAttribute('aria-expanded', 'true');
                tl.play();
            };

            const onLeave = () => {
                this.header.removeAttribute('aria-expanded');
                tl.reverse();
            };

            nav.addEventListener('mouseenter', onEnter);
            this.header.addEventListener('mouseleave', onLeave);

            window.addEventListener('resize', () => {
                if (window.innerWidth <= 1024) {
                    this.header.removeAttribute('style');
                    this.header.removeAttribute('aria-expanded');
                }
            });
        }
    }

    initDrawer() {
        if (!this.drawer) return;

        const drawerOpenBtn = this.header.querySelector('.no-header__toggle');
        const drawerCloseBtn = this.drawer.querySelector('.no-drawer__close, #drawer-close-btn');

        if (typeof gsap !== 'undefined') {
            // 초기 상태 설정 (깜빡임 방지)
            gsap.set(this.drawer, {
                y: '-100%',
                opacity: 0
            });
            this.drawer.style.visibility = 'hidden';
            this.drawer.style.pointerEvents = 'none';

            const drawerTl = gsap.timeline({ paused: true });
            drawerTl.to(this.drawer, {
                y: '0%',
                opacity: 1,
                duration: 0.5,
                ease: 'power3.out',
            });

            const onOpen = () => {
                document.body.classList.add('--hidden');
                document.body.style.overflow = 'hidden';
                this.drawer.style.visibility = 'visible';
                this.drawer.style.pointerEvents = 'auto';
                this.drawer.classList.add('is-open');
                drawerTl.play();
            };

            const onClose = () => {
                document.body.classList.remove('--hidden');
                document.body.style.overflow = '';
                this.drawer.classList.remove('is-open');
                drawerTl.reverse();
                
                // 애니메이션 완료 후 숨김 처리
                drawerTl.eventCallback('onReverseComplete', () => {
                    this.drawer.style.visibility = 'hidden';
                    this.drawer.style.pointerEvents = 'none';
                });
                
                if (window.drawerInstance) {
                    window.drawerInstance.closeAllSubmenus();
                }
            };

            if (drawerOpenBtn) {
                drawerOpenBtn.addEventListener('click', onOpen);
            }

            if (drawerCloseBtn) {
                drawerCloseBtn.addEventListener('click', onClose);
            }

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
                    onClose();
                }
            });
        }
    }
}

